import { useState } from 'react';
import { Avatar, Box, Button, Card, CardContent, Chip, Stack, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeOutlined from '@mui/icons-material/AutoAwesomeOutlined';
import OpenInNew from '@mui/icons-material/OpenInNew';
import PictureAsPdfOutlined from '@mui/icons-material/PictureAsPdfOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { EmptyState } from '../../components/EmptyState';
import { useNotice } from '../../components/NoticeProvider';
import { StatusChip } from '../../components/StatusChip';
import { TabStrip } from '../../components/TabStrip';
import { ModeIcon, RouteText } from '../shipments/ShipmentCard';
import { STAGE_TONE, type Shipment } from '../shipments/sample';
import { PARTIES, type Conversation, type Message, type PartyKey } from './sample';

type Props = {
  conv: Conversation;
  ship: Shipment;
  party: PartyKey;
  onParty: (p: PartyKey) => void;
  /** notes you added in this session, for this party */
  notes: Message[];
  onAddNote: (text: string) => void;
};

/** One shipment's conversations: summary, who you are talking to, the messages, and a box for internal notes */
export function ThreadView({ conv, ship, party, onParty, notes, onAddNote }: Props) {
  const thread = conv.threads[party];
  const all = [...thread.messages, ...notes];
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
        <Avatar sx={{ width: 48, height: 48, bgcolor: 'action.selected', color: 'text.secondary' }}>
          <ModeIcon mode={ship.mode} />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 750 }}>
              {conv.srb}
            </Typography>
            <Chip size="small" label={ship.mode} />
            <StatusChip label={ship.stage} tone={STAGE_TONE[ship.stage]} />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            <RouteText s={ship} />
          </Typography>
        </Box>
        <Button component={RouterLink} to={`/shipments/${conv.srb}`} variant="outlined">
          View Shipment
        </Button>
      </Stack>

      <Card sx={{ bgcolor: 'action.hover' }}>
        <CardContent>
          <Stack direction="row" spacing={1} sx={{ justifyContent: 'space-between' }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', color: 'primary.main' }}>
              <AutoAwesomeOutlined fontSize="small" />
              <Typography sx={{ fontWeight: 700 }}>AI Communication Summary</Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Last updated {conv.last}
            </Typography>
          </Stack>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {conv.ai}
          </Typography>
        </CardContent>
      </Card>

      <TabStrip
        tabs={PARTIES.map((p) => ({ value: p, label: `${p} (${conv.threads[p].messages.length})` }))}
        value={party}
        onChange={onParty}
      />

      <Stack
        direction="row"
        spacing={1.5}
        sx={{ alignItems: 'center', p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700 }}>{party}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
            {thread.contact || 'No contact on file yet'}
          </Typography>
        </Box>
      </Stack>

      {all.length ? <Messages messages={all} /> : <EmptyState>No messages yet.</EmptyState>}
      <NoteBox onSave={onAddNote} />
    </Stack>
  );
}

function Messages({ messages }: { messages: Message[] }) {
  const notice = useNotice();
  return (
    <Stack spacing={1.5}>
      {messages.map((m, i) => (
        <Stack key={m.id} spacing={1.5}>
          {(i === 0 || messages[i - 1]!.date !== m.date) && (
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
              {m.date}
            </Typography>
          )}
          <Stack direction="row" spacing={1.5}>
            <Avatar
              sx={{
                width: 36,
                height: 36,
                fontSize: 13,
                bgcolor: m.from === 'You' ? 'text.primary' : 'action.selected',
                color: m.from === 'You' ? 'background.paper' : 'text.secondary',
              }}
            >
              {m.from === 'You'
                ? 'I'
                : m.from
                    .split(' ')
                    .map((w) => w[0])
                    .join('')
                    .slice(0, 2)}
            </Avatar>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                p: 1.5,
                borderRadius: 2,
                bgcolor: m.internal ? 'warning.light' : 'action.hover',
                color: m.internal ? 'warning.contrastText' : undefined,
              }}
            >
              <Stack direction="row" spacing={1} sx={{ justifyContent: 'space-between' }}>
                <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                  {m.from}
                  {m.internal && ' (internal note, not sent)'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {m.time}
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>
                {m.text}
              </Typography>
              {m.attachments && (
                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}>
                  {m.attachments.map((a) => (
                    <Chip
                      key={a.name}
                      variant="outlined"
                      icon={<PictureAsPdfOutlined color="error" />}
                      label={`${a.name} · ${a.size}`}
                    />
                  ))}
                </Stack>
              )}
              {!m.internal && (
                <Button
                  size="small"
                  endIcon={<OpenInNew fontSize="small" />}
                  onClick={() => notice('The original email opens once the mailbox is connected.', 'info')}
                >
                  View Original
                </Button>
              )}
            </Box>
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
}

/** A note for yourself. It is kept with the conversation and is never sent to anyone. */
function NoteBox({ onSave }: { onSave: (text: string) => void }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  if (!open) {
    return (
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={() => setOpen(true)}
        sx={{ borderStyle: 'dashed', py: 1.5, flexDirection: 'column' }}
      >
        Add Internal Note
        <Typography variant="caption" color="text.secondary">
          Add a summary, follow-up, or important information (not sent to agent).
        </Typography>
      </Button>
    );
  }
  return (
    <Stack spacing={1}>
      <TextField
        autoFocus
        multiline
        minRows={3}
        label="Internal note (not sent to anyone)"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
        <Button
          onClick={() => {
            setOpen(false);
            setText('');
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!text.trim()}
          onClick={() => {
            onSave(text.trim());
            setOpen(false);
            setText('');
          }}
        >
          Save Note
        </Button>
      </Stack>
    </Stack>
  );
}
